import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Car,
    FileCheck,
    Eye,
    EyeOff,
    ArrowRight,
    Sun,
    Moon,
    CheckCircle,
    Clock,
    Users,
    Zap
} from 'lucide-react';

export default function Landing() {
    const { login, register, loginWithGoogle, isAuthenticated, user } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    // Login form
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    // Register form
    const [regName, setRegName] = useState('');
    const [regEmail, setRegEmail] = useState('');
    const [regPassword, setRegPassword] = useState('');
    const [regConfirmPassword, setRegConfirmPassword] = useState('');
    const [regPhone, setRegPhone] = useState('');

    // Password visibility toggles
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegPassword, setShowRegPassword] = useState(false);
    const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);

    // Live mismatch check — true once the user has typed a confirmation that differs.
    const passwordsMismatch = regConfirmPassword.length > 0 && regConfirmPassword !== regPassword;

    // Redirect if already authenticated
    React.useEffect(() => {
        if (isAuthenticated && user) {
            if (user.role === 'admin') navigate('/admin');
            else if (user.role === 'officer') navigate('/officer');
            else if (user.role === 'officer_pending' || user.role === 'officer_rejected') navigate('/pending-approval');
            else navigate('/dashboard');
        }
    }, [isAuthenticated, user, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const userData = await login(loginEmail, loginPassword);
            toast.success('Welcome back!');
            if (userData.role === 'admin') navigate('/admin');
            else if (userData.role === 'officer') navigate('/officer');
            else if (userData.role === 'officer_pending' || userData.role === 'officer_rejected') navigate('/pending-approval');
            else navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (regPassword !== regConfirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setIsLoading(true);
        try {
            await register(regName, regEmail, regPassword, regPhone);
            toast.success('Account created successfully!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const features = [
        {
            icon: Car,
            title: 'Report Violations',
            description: 'Easily report traffic violations with photos and videos'
        },
        {
            icon: Zap,
            title: 'AI License Plate Detection',
            description: 'Automatic number plate recognition using OCR technology'
        },
        {
            icon: Eye,
            title: 'Track Status',
            description: 'Real-time updates on your reported violations'
        },
        {
            icon: FileCheck,
            title: 'Quick Processing',
            description: 'Efficient review by traffic police officers'
        }
    ];

    const stats = [
        { value: '10K+', label: 'Reports Filed', icon: FileCheck },
        { value: '95%', label: 'Approval Rate', icon: CheckCircle },
        { value: '<24h', label: 'Avg. Review Time', icon: Clock },
        { value: '50+', label: 'Cities Covered', icon: Users }
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Theme Toggle */}
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="fixed top-4 right-4 z-50"
                data-testid="theme-toggle-landing"
            >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center">
                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1718634953659-a2ed9b1c119b?w=1920&q=80)',
                    }}
                >
                    <div className={`absolute inset-0 ${theme === 'dark' ? 'bg-gradient-to-r from-background via-background/95 to-background/70' : 'bg-gradient-to-r from-white via-white/98 to-white/90'}`} />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Left Content */}
                        <div className="space-y-8">
                            <div className="flex items-center gap-3">
                                <img
                                    src="/logo.png"
                                    alt="TrafficWatch logo"
                                    className="h-14 w-auto rounded-lg shadow-sm"
                                />
                                <span className="text-2xl font-bold tracking-tight">TrafficWatch</span>
                            </div>

                            <div className="space-y-4">
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none">
                                    Report Traffic
                                    <br />
                                    <span className="text-primary">Violations</span>
                                    <br />
                                    Instantly
                                </h1>
                                <p className="text-lg text-muted-foreground max-w-md">
                                    Help make roads safer. Report traffic violations with evidence and track
                                    them through our streamlined verification process.
                                </p>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {stats.map((stat, idx) => (
                                    <div
                                        key={idx}
                                        className="text-center p-4 rounded-lg bg-card/50 border border-border/50 backdrop-blur-sm"
                                    >
                                        <p className="text-2xl font-bold text-primary">{stat.value}</p>
                                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right - Auth Card */}
                        <div className="lg:pl-12">
                            <Card className="w-full max-w-md mx-auto shadow-2xl border-border/50 bg-card/95 backdrop-blur-xl">
                                <CardHeader className="text-center pb-2">
                                    <CardTitle className="text-2xl">Get Started</CardTitle>
                                    <CardDescription>
                                        Sign in or create an account to report violations
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="pt-0">
                                    <Tabs defaultValue="login" className="w-full">
                                        <TabsList className="grid w-full grid-cols-2 mb-4">
                                            <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                                            <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                                        </TabsList>

                                        <TabsContent value="login" className="space-y-4">
                                            <form onSubmit={handleLogin} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="login-email">Email</Label>
                                                    <Input
                                                        id="login-email"
                                                        type="email"
                                                        placeholder="name@example.com"
                                                        value={loginEmail}
                                                        onChange={(e) => setLoginEmail(e.target.value)}
                                                        required
                                                        data-testid="login-email-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="login-password">Password</Label>
                                                        <Button
                                                            type="button"
                                                            variant="link"
                                                            className="px-0 h-auto text-xs"
                                                            onClick={() => navigate('/forgot-password')}
                                                            data-testid="forgot-password-link"
                                                        >
                                                            Forgot password?
                                                        </Button>
                                                    </div>
                                                    <div className="relative">
                                                        <Input
                                                            id="login-password"
                                                            type={showLoginPassword ? 'text' : 'password'}
                                                            placeholder="••••••••"
                                                            value={loginPassword}
                                                            onChange={(e) => setLoginPassword(e.target.value)}
                                                            required
                                                            className="pr-10"
                                                            data-testid="login-password-input"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowLoginPassword((v) => !v)}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                            aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                                                            tabIndex={-1}
                                                            data-testid="login-password-toggle"
                                                        >
                                                            {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="submit"
                                                    className="w-full"
                                                    disabled={isLoading}
                                                    data-testid="login-submit-btn"
                                                >
                                                    {isLoading ? 'Signing in...' : 'Sign In'}
                                                    <ArrowRight className="w-4 h-4 ml-2" />
                                                </Button>
                                            </form>

                                            <div className="relative">
                                                <div className="absolute inset-0 flex items-center">
                                                    <span className="w-full border-t" />
                                                </div>
                                                <div className="relative flex justify-center text-xs uppercase">
                                                    <span className="bg-card px-2 text-muted-foreground">Or</span>
                                                </div>
                                            </div>

                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                onClick={loginWithGoogle}
                                                data-testid="google-login-btn"
                                            >
                                                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                                </svg>
                                                Continue with Google
                                            </Button>
                                        </TabsContent>

                                        <TabsContent value="register" className="space-y-4">
                                            <form onSubmit={handleRegister} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="reg-name">Full Name</Label>
                                                    <Input
                                                        id="reg-name"
                                                        type="text"
                                                        placeholder="John Doe"
                                                        value={regName}
                                                        onChange={(e) => setRegName(e.target.value)}
                                                        required
                                                        data-testid="register-name-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="reg-email">Email</Label>
                                                    <Input
                                                        id="reg-email"
                                                        type="email"
                                                        placeholder="name@example.com"
                                                        value={regEmail}
                                                        onChange={(e) => setRegEmail(e.target.value)}
                                                        required
                                                        data-testid="register-email-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="reg-phone">Phone (Optional)</Label>
                                                    <Input
                                                        id="reg-phone"
                                                        type="tel"
                                                        placeholder="+91 98765 43210"
                                                        value={regPhone}
                                                        onChange={(e) => setRegPhone(e.target.value)}
                                                        data-testid="register-phone-input"
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="reg-password">Password</Label>
                                                    <div className="relative">
                                                        <Input
                                                            id="reg-password"
                                                            type={showRegPassword ? 'text' : 'password'}
                                                            placeholder="••••••••"
                                                            value={regPassword}
                                                            onChange={(e) => setRegPassword(e.target.value)}
                                                            required
                                                            minLength={6}
                                                            className="pr-10"
                                                            data-testid="register-password-input"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowRegPassword((v) => !v)}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                            aria-label={showRegPassword ? 'Hide password' : 'Show password'}
                                                            tabIndex={-1}
                                                            data-testid="register-password-toggle"
                                                        >
                                                            {showRegPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="reg-confirm-password">Retype Password</Label>
                                                        {passwordsMismatch && (
                                                            <span
                                                                className="text-xs font-medium text-destructive"
                                                                data-testid="password-mismatch-error"
                                                            >
                                                                Password not matching
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="relative">
                                                        <Input
                                                            id="reg-confirm-password"
                                                            type={showRegConfirmPassword ? 'text' : 'password'}
                                                            placeholder="••••••••"
                                                            value={regConfirmPassword}
                                                            onChange={(e) => setRegConfirmPassword(e.target.value)}
                                                            required
                                                            minLength={6}
                                                            className={`pr-10 ${passwordsMismatch ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                                                            data-testid="register-confirm-password-input"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowRegConfirmPassword((v) => !v)}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                            aria-label={showRegConfirmPassword ? 'Hide password' : 'Show password'}
                                                            tabIndex={-1}
                                                            data-testid="register-confirm-password-toggle"
                                                        >
                                                            {showRegConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="submit"
                                                    className="w-full"
                                                    disabled={isLoading || passwordsMismatch}
                                                    data-testid="register-submit-btn"
                                                >
                                                    {isLoading ? 'Creating Account...' : 'Create Account'}
                                                    <ArrowRight className="w-4 h-4 ml-2" />
                                                </Button>
                                            </form>

                                            <div className="relative">
                                                <div className="absolute inset-0 flex items-center">
                                                    <span className="w-full border-t" />
                                                </div>
                                                <div className="relative flex justify-center text-xs uppercase">
                                                    <span className="bg-card px-2 text-muted-foreground">Or</span>
                                                </div>
                                            </div>

                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                onClick={loginWithGoogle}
                                                data-testid="google-register-btn"
                                            >
                                                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                                                </svg>
                                                Continue with Google
                                            </Button>
                                        </TabsContent>
                                    </Tabs>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-muted/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <p className="overline mb-2">Features</p>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                            How It Works
                        </h2>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
                        {features.map((feature, idx) => (
                            <Card
                                key={idx}
                                className="group hover:border-primary/50 transition-colors"
                            >
                                <CardContent className="p-6">
                                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 border-t">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <img src="/logo.png" alt="" className="h-6 w-auto rounded" />
                            <span className="font-semibold">TrafficWatch</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            © 2024 Traffic Resolution Inc. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
